import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Table} from '@/components/commons/table/Table'
import {Button} from '@/components/ui/button/Button'
import {FiltersBar} from '@/components/ui/filters-bar/FiltersBar'
import {AlertTriangleIcon, Download01Icon, SquareIcon} from '@/components/ui/icon/Icon'
import i18n, {TKey, useTranslation} from '@/translations/i18n'
import {z} from 'zod'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {generatePath, Link, useNavigate, useOutletContext} from 'react-router-dom'
import {useCallback, useState} from 'react'
import {PatientExtended, PatientEventsColumn} from '@/features/patients/types'
import {useGetPatientEvents} from '@/features/patients/services/useGetPatientEvents'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload
} from '@/utilities/helpers'
import {TableBodyCell, TableBodyCellText, TableBodyRow} from '@/components/ui/table-atoms/TableAtoms'
import {SelectRowCheckbox} from '@/components/ui/select-row-checkbox/SelectRowCheckbox'
import {SelectColumnHeader} from '@/components/ui/select-column-header/SelectColumnHeader'
import {usePermissions} from '@/features/auth/store/store'
import {StyledPatientDetailsEventsWrapper, StyledSelectCell} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {SelectedRowsText} from '@/components/commons/selected-rows-text/SelectedRowsText'
import {httpExportPatientEvents} from '@/features/patients/services/patients.http'
import {TableFilters} from '@/components/commons/table-filters/TableFilters'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {ColumnVisibilityMenu} from '@/components/commons/columns-visibility-menu/ColumnVisibilityMenu'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {agendaEventStatusMapToColor, agendaEventStatusMapToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {projectSectorToLabel} from '@/features/projects/utils'
import {routes} from '@/utilities/constants/routes'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useTheme} from 'styled-components'
import {EventSmall} from '@/features/agenda/types'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

const columnNameToLabel = {
    [PatientEventsColumn.enum.date]: 'patients:event_date',
    [PatientEventsColumn.enum.time]: 'commons:time',
    [PatientEventsColumn.enum.status]: 'commons:status',
    [PatientEventsColumn.enum.accessType]: 'agenda:event_type',
    [PatientEventsColumn.enum.consultantId]: 'commons:operator',
    [PatientEventsColumn.enum.sector]: 'accesses:mode',
    [PatientEventsColumn.enum.isRemote]: 'accesses:remote',
    [PatientEventsColumn.enum.projectCode]: 'commons:pri_n',
    [PatientEventsColumn.enum.projectStartDate]: 'projects:start_date',
    [PatientEventsColumn.enum.projectExpirationDate]: 'commons:expiration'
} as const satisfies Record<Exclude<PatientEventsColumn, 'select'>, TKey | null>

const makeColumnsMap = () =>
    [
        {
            id: PatientEventsColumn.enum.date,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.date])
        },
        {
            id: PatientEventsColumn.enum.time,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.time])
        },
        {
            id: PatientEventsColumn.enum.status,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.status])
        },
        {
            id: PatientEventsColumn.enum.accessType,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.accessType])
        },
        {
            id: PatientEventsColumn.enum.consultantId,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.consultantId])
        },
        {
            id: PatientEventsColumn.enum.sector,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.sector])
        },
        {
            id: PatientEventsColumn.enum.isRemote,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.isRemote])
        },
        {
            id: PatientEventsColumn.enum.projectCode,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.projectCode])
        },
        {
            id: PatientEventsColumn.enum.projectStartDate,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.projectStartDate])
        },
        {
            id: PatientEventsColumn.enum.projectExpirationDate,
            label: i18n.t(columnNameToLabel[PatientEventsColumn.enum.projectExpirationDate])
        }
    ] as const satisfies {id: PatientEventsColumn; label: string}[]

export const PatientDetailsEvents = () => {
    const {t} = useTranslation()
    const {spacing} = useTheme()
    const permissions = usePermissions()
    const columnsMap = makeColumnsMap()
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedEventsIds, setUnselectedEventsIds] = useState<string[]>([])
    const [selectedEventsIds, setSelectedEventsIds] = useState<string[]>([])

    const {patient} = useOutletContext<{patient: PatientExtended}>()
    const {searchParams, setSearchParams} = useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            filters: z.string().optional(),
            sorts: z.string().optional(),
            columnVisibility: z.string().optional()
        })
    )
    const visitsQuery = useGetPatientEvents(
        {patientId: patient.id},
        {
            limit: searchParams.limit,
            page: searchParams.page
        },
        {
            filters: searchParams.filters ? JSON.parse(searchParams.filters) : undefined,
            sorts: searchParams.sorts ? JSON.parse(searchParams.sorts) : undefined
        }
    )

    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedEventsIds([])
        setUnselectedEventsIds([])
    }

    const selectedPatientEventsCount = isSelectAll
        ? visitsQuery.persistedPagination.total - unselectedEventsIds.length
        : selectedEventsIds.length

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(PatientEventsColumn, searchParams.columnVisibility) ??
        Object.fromEntries(PatientEventsColumn.options.map(column => [column, true]))

    const getPatientProject = useCallback(
        (event: EventSmall): NonNullable<EventSmall['eventPatients']>[number]['project'] | undefined => {
            return event.eventPatients?.find(patient => patient.patientId == patient.patientId)?.project ?? undefined
        },
        [patient.id]
    )

    const navigate = useNavigate()

    return (
        <StyledPatientDetailsEventsWrapper>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    {selectedPatientEventsCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedPatientEventsCount} />
                            <ExportModal
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                                fileName="Visite paziente"
                                promise={({extension, mode}) =>
                                    httpExportPatientEvents({
                                        urlParams: {patientId: patient.id},
                                        payload: {
                                            selectedVisitIds: selectedEventsIds,
                                            exceptedVisitIds: unselectedEventsIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'view'
                                                    ? PatientEventsColumn.exclude(['select']).options.filter(
                                                          column => columnVisibility[column]
                                                      )
                                                    : PatientEventsColumn.exclude(['select']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }
                                selectedCount={selectedPatientEventsCount}
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                            />
                        </>
                    )}
                </Flexbox>
                {selectedPatientEventsCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="patientEvents"
                            availableFilters={[
                                {
                                    key: PatientEventsColumn.enum.date,
                                    label: columnNameToLabel[PatientEventsColumn.enum.date]
                                },
                                {
                                    key: PatientEventsColumn.enum.time,
                                    label: columnNameToLabel[PatientEventsColumn.enum.time]
                                },
                                {
                                    key: PatientEventsColumn.enum.status,
                                    label: columnNameToLabel[PatientEventsColumn.enum.status]
                                },
                                {
                                    key: PatientEventsColumn.enum.accessType,
                                    label: columnNameToLabel[PatientEventsColumn.enum.accessType]
                                },
                                {
                                    key: PatientEventsColumn.enum.consultantId,
                                    label: columnNameToLabel[PatientEventsColumn.enum.consultantId]
                                },
                                {
                                    key: PatientEventsColumn.enum.isRemote,
                                    label: columnNameToLabel[PatientEventsColumn.enum.isRemote]
                                },
                                {
                                    key: PatientEventsColumn.enum.projectCode,
                                    label: columnNameToLabel[PatientEventsColumn.enum.projectCode]
                                },
                                {
                                    key: PatientEventsColumn.enum.projectStartDate,
                                    label: columnNameToLabel[PatientEventsColumn.enum.projectStartDate]
                                },
                                {
                                    key: PatientEventsColumn.enum.projectExpirationDate,
                                    label: columnNameToLabel[PatientEventsColumn.enum.projectExpirationDate]
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap.filter(column => column.id != PatientEventsColumn.enum.consultantId)}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {visitsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('patients:no_visits_title')}
                            subtitle={searchParams.filters ? t('patients:no_visits_description') : undefined}
                        >
                            {searchParams.filters && (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchParams({filters: undefined, page: 1})
                                    }}
                                >
                                    {t('patients:no_visits_cta')}
                                </Button>
                            )}
                        </EmptyState>
                    }
                    pagination={{
                        ...visitsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [PatientEventsColumn.enum.select],
                        right: []
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedEventsIds,
                            unselectedIds: unselectedEventsIds,
                            isSelectAll
                        })
                    }
                    isLoading={visitsQuery.isPending}
                    data={visitsQuery.data?.data ?? []}
                    rowComponent={({tableBodyRowProps, index, dataItem}) => (
                        <TableBodyRow
                            key={index}
                            onClick={() =>
                                dataItem
                                    ? navigate({
                                          pathname: generatePath(routes.AGENDA.path, {
                                              entity: 'patients',
                                              view: 'day',
                                              date: dayjs(dataItem?.date).format('DD-MM-YYYY')
                                          }),
                                          search: new URLSearchParams({
                                              category: 'custom',
                                              subjectIds: patient.id
                                          }).toString()
                                      })
                                    : undefined
                            }
                            {...tableBodyRowProps}
                        />
                    )}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        {permissions?.patients.write && (
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedEventsIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedEventsIds,
                                                        unselectedIds: unselectedEventsIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedEventsIds}
                                            />
                                        )}
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.patients.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={visitsQuery.data?.data.map(visit => visit.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedEventsIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedEventsIds}
                                        setUnselectedIds={setUnselectedEventsIds}
                                        setSelectedIds={setSelectedEventsIds}
                                        selectedCount={selectedPatientEventsCount}
                                        paginationTotal={visitsQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: PatientEventsColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.date, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.date]),
                            id: PatientEventsColumn.enum.date,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.time
                                            ? formatLocaleDate(
                                                  `${formatLocaleDate(dataItem.date, 'YYYY-MM-DD')} ${dataItem.time}`,
                                                  'HH:mm'
                                              )
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.time]),
                            id: PatientEventsColumn.enum.time,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.status ? (
                                        <Flexbox align="center" gap={2}>
                                            <SquareIcon
                                                className="custom"
                                                size={8}
                                                color={agendaEventStatusMapToColor[dataItem.status]}
                                            />
                                            <TableBodyCellText>
                                                {t(agendaEventStatusMapToLabel[dataItem.status])}
                                            </TableBodyCellText>
                                        </Flexbox>
                                    ) : (
                                        '-'
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.status]),
                            id: PatientEventsColumn.enum.status,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText weight="bold">
                                        {dataItem.eventConsultants?.[0]?.accessType?.accessType
                                            ? t(eventTypeMapToLabel[dataItem.eventConsultants[0].accessType.accessType])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.accessType]),
                            id: PatientEventsColumn.enum.accessType,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => {
                                const consultants = dataItem.eventConsultants ?? []

                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        {consultants.length === 0 ? (
                                            <TableBodyCellText variant="normal">{t('commons:none')}</TableBodyCellText>
                                        ) : (
                                            <Flexbox align="center" gap={1.5} fullWidth>
                                                {consultants.slice(0, 2).map((consultant, index) => (
                                                    <Avatar
                                                        key={consultant.userId}
                                                        style={
                                                            index > 0
                                                                ? {marginLeft: -(spacing * 4)}
                                                                : {position: 'relative'}
                                                        }
                                                        size={consultants.length >= 2 ? 'xs' : 'sm'}
                                                        name={consultant.user.fullName}
                                                    />
                                                ))}
                                                <Tooltip
                                                    rootProps={{
                                                        open: consultants.length < 2 ? false : undefined
                                                    }}
                                                    variant="light"
                                                    triggerProps={{
                                                        style: {
                                                            width: '100%',
                                                            overflow: 'hidden'
                                                        }
                                                    }}
                                                    content={consultants.map(consultant => (
                                                        <span key={consultant.userId}>{consultant.user.fullName}</span>
                                                    ))}
                                                >
                                                    <TableBodyCellText variant="dark">
                                                        {consultants.length >= 2
                                                            ? t('treatment_requests:x_therapists', {
                                                                  count: consultants.length
                                                              })
                                                            : consultants[0].user.fullName}
                                                    </TableBodyCellText>
                                                </Tooltip>
                                            </Flexbox>
                                        )}
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.consultantId]),
                            id: PatientEventsColumn.enum.consultantId,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.sector ? t(projectSectorToLabel[dataItem.sector]) : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.sector]),
                            id: PatientEventsColumn.enum.sector,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.isRemote ? t('commons:yes') : t('commons:no')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[PatientEventsColumn.enum.isRemote]),
                            id: PatientEventsColumn.enum.isRemote,
                            size: () => 140
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getPatientProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {permissions?.projects?.read && project ? (
                                                <Link
                                                    target="_blank"
                                                    to={generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {
                                                        id: project.id
                                                    })}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    {project.code}
                                                </Link>
                                            ) : (
                                                (project?.code ?? '-')
                                            )}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('commons:pri_n'),
                            id: PatientEventsColumn.enum.projectCode,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getPatientProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {project?.startDate ? formatLocaleDate(project.startDate, 'll') : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:pri_start_date'),
                            id: PatientEventsColumn.enum.projectStartDate,
                            size: () => 100,
                            minSize: () => 60
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const project = getPatientProject(dataItem)
                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TableBodyCellText>
                                            {project?.expirationDate
                                                ? formatLocaleDate(project.expirationDate, 'll')
                                                : '-'}
                                        </TableBodyCellText>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t('accesses:pri_expiration_date'),
                            id: PatientEventsColumn.enum.projectExpirationDate,
                            size: () => 120,
                            minSize: () => 80
                        }
                    ]}
                />
            )}
        </StyledPatientDetailsEventsWrapper>
    )
}
